// The head of «Профиль» (decision 038 п.1): the person's picture and name, changed by themselves — the name by «user.setProfile» on
// leaving the field or Enter, the picture uploaded as a file of the team and shown by its thumbnail. Nobody changes them for another.

import { useRef, useState } from 'react';
import { avatarUrl, type UserDef } from '@confdeck/core-shared';
import { useSync } from '../sync/react.tsx';
import { Avatar, Button, Input, useToast } from '../ui/primitives.tsx';
import s from '../team/team.module.css';
import p from './settings.module.css';

export function ProfileHead({ me }: { me: UserDef }) {
  const client = useSync();
  const toast = useToast();
  const [name, setName] = useState(me.name);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const saveName = async () => {
    const v = name.trim();
    if (!v) { setName(me.name); toast({ kind: 'error', text: 'Имя не может быть пустым' }); return; }
    if (v === me.name) return;
    const r = await client.run('user.setProfile', { name: v });
    toast(r.ok ? { kind: 'info', text: 'Имя сохранено' } : { kind: 'error', text: r.error.message });
    if (!r.ok) setName(me.name);
  };
  const upload = async (f: File) => {
    setBusy(true);
    try {
      const r = await fetch('/api/me/avatar', { method: 'POST', headers: { 'content-type': 'application/octet-stream' }, body: f });
      const body = (await r.json().catch(() => null)) as { error?: { message?: string } } | null;
      toast(r.ok ? { kind: 'info', text: 'Фото обновлено' } : { kind: 'error', text: body?.error?.message ?? 'Не удалось загрузить фото' });
    } catch { toast({ kind: 'error', text: 'Нет связи с сервером — фото не загружено' }); }
    finally { setBusy(false); if (file.current) file.current.value = ''; }
  };
  // «Убрать» goes through the server's route: the profile stops naming the picture and its bytes leave the storage.
  const drop = async () => {
    setBusy(true);
    try {
      const r = await fetch('/api/me/avatar', { method: 'DELETE' });
      const body = (await r.json().catch(() => null)) as { error?: { message?: string } } | null;
      toast(r.ok ? { kind: 'info', text: 'Фото убрано' } : { kind: 'error', text: body?.error?.message ?? 'Не удалось убрать фото' });
    } catch { toast({ kind: 'error', text: 'Нет связи с сервером — фото не убрано' }); }
    finally { setBusy(false); }
  };
  return (
    <div className={p.who}>
      <Avatar name={me.name} color={me.color} src={avatarUrl(me)} size={64} />
      <div className={p.whoBody}>
        <Input bare className={p.name} value={name} aria-label="Имя и фамилия" maxLength={120} onChange={(e) => setName(e.target.value)} onBlur={() => void saveName()} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setName(me.name); }} />
        <p className={s.help}>{me.email}</p>
        <div className={s.actions}>
          <input ref={file} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden aria-label="Файл фото" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
          <Button size="sm" variant="secondary" icon="fileImage" disabled={busy} onClick={() => file.current?.click()}>{busy ? 'Загружаем…' : me.avatar ? 'Сменить фото' : 'Загрузить фото'}</Button>
          {me.avatar && <Button size="sm" variant="ghost" disabled={busy} onClick={() => void drop()}>Убрать</Button>}
          <span className={s.help}>JPEG, PNG, WebP или GIF до 5 МБ</span>
        </div>
      </div>
    </div>
  );
}
